import { Link } from 'react-router-dom'
import type { ColDef } from 'ag-grid-community'
import { RequestStatusBadge } from '../../../shared/ui/Badge'
import { CheckboxSetFilter } from '../../table/CheckboxSetFilter'
import { ProcStatusBadge } from './ProcStatusBadge'
import { RequestsDeleteButton } from './RequestsDeleteButton'
import type { RequestStatus, Request } from '../../../shared/api/types'
import { REQUEST_STATUS_LABELS_RU } from '@crm/shared'

// Подписи статусов заявки — единый источник в @crm/shared.
export const STATUS_LABELS: Record<RequestStatus, string> = REQUEST_STATUS_LABELS_RU

export const STATUS_OPTIONS: { value: RequestStatus | ''; label: string }[] = [
  { value: '', label: 'Все статусы' },
  { value: 'NEW', label: 'Новые' },
  { value: 'SENT_TO_PROCUREMENT', label: 'В закупку' },
  { value: 'IN_PROGRESS', label: 'В работе' },
  { value: 'PARTIALLY_PROCESSED', label: 'Частично' },
  { value: 'QUOTATION_READY', label: 'Готовы к КП' },
  { value: 'QUOTATION_CREATED', label: 'КП выставлено' },
  { value: 'QUOTE_SENT', label: 'КП отправлено' },
  { value: 'CLOSED_WON', label: 'Сделки' },
  { value: 'CLOSED_LOST', label: 'Отказы' },
  { value: 'ARCHIVED', label: 'Архив' },
]

export function formatSum(value: number): string {
  return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 }).format(value) + ' ₽'
}

export function formatDate(iso: string | null): string {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString('ru-RU')
}

type RowData = {
  id: string
  clientName: string
  clientCode: string
  requestNumber: string
  type: string
  deadline: string | null
  deadlineLabel: string
  followUpDueAt: string | null
  followUpLabel: string
  followUpNote: string | null
  status: RequestStatus
  statusLabel: string
  itemsCount: number
  quotationSum: number | null
  quotationSumLabel: string
  managerName: string
  createdAt: string
  createdAtLabel: string
  procQuotesTotal: number
  procQuotesByMe: number
  procQuotesLabel: string
  procQuotesDone: boolean
  procurementProgressLabel: string
  procurementProgressTone: string
  _req: Request
}

export const defaultColDef: ColDef = {
  sortable: true,
  resizable: true,
  suppressMovable: false,
  filter: CheckboxSetFilter,
  floatingFilter: false,
}

interface BuildColDefsParams {
  canDelete: boolean
  showManagerColumn: boolean
  isProcurement: boolean
  onDeleted: () => void
}

export function buildRequestsColDefs({
  canDelete,
  showManagerColumn,
  isProcurement,
  onDeleted,
}: BuildColDefsParams): ColDef[] {
  return [
    ...(!isProcurement
      ? [
          {
            field: 'clientName',
            headerName: 'Клиент',
            minWidth: 150,
            tooltipField: 'clientName',
            cellRenderer: ({ data }: { data: RowData }) => (
              <Link
                to={
                  data.status === 'DRAFT'
                    ? `/requests/new?draftId=${data.id}`
                    : `/requests/${data.id}`
                }
                className="text-brand-600 hover:text-brand-700 font-medium"
              >
                {data.clientName}
              </Link>
            ),
          } as ColDef,
        ]
      : []),
    ...(isProcurement
      ? [
          {
            field: 'clientCode',
            headerName: 'Код клиента',
            minWidth: 130,
            cellRenderer: ({ data }: { data: RowData }) => (
              <Link
                to={
                  data.status === 'DRAFT'
                    ? `/requests/new?draftId=${data.id}`
                    : `/requests/${data.id}`
                }
                className="text-brand-600 hover:text-brand-700 font-mono font-medium"
              >
                {data.clientCode}
              </Link>
            ),
          } as ColDef,
        ]
      : []),
    {
      field: 'requestNumber',
      headerName: 'Заявка',
      minWidth: 120,
      cellRenderer: ({ data }: { data: RowData }) => (
        <Link
          to={data.status === 'DRAFT' ? `/requests/new?draftId=${data.id}` : `/requests/${data.id}`}
          className="text-gray-700 hover:text-brand-600 font-medium"
        >
          {data.requestNumber}
        </Link>
      ),
    },
    { field: 'type', headerName: 'Тип', minWidth: 90 },
    {
      field: 'deadlineLabel',
      headerName: 'Дедлайн',
      minWidth: 110,
      filterParams: { dateMode: true },
      comparator: (_a: string, _b: string, nodeA, nodeB) => {
        const da = (nodeA?.data as RowData | undefined)?.deadline
        const db = (nodeB?.data as RowData | undefined)?.deadline
        const ta = da ? new Date(da).getTime() : 0
        const tb = db ? new Date(db).getTime() : 0
        return ta - tb
      },
      cellRenderer: ({ data }: { data: RowData }) => {
        if (!data.deadline) return <span className="text-gray-400">—</span>
        const date = new Date(data.deadline)
        const now = new Date()
        now.setHours(0, 0, 0, 0)
        date.setHours(0, 0, 0, 0)
        const diffDays = Math.ceil((date.getTime() - now.getTime()) / 86400000)
        if (diffDays < 0)
          return (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
              {data.deadlineLabel}
            </span>
          )
        if (diffDays <= 3)
          return (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
              {data.deadlineLabel}
            </span>
          )
        return <span className="text-gray-700">{data.deadlineLabel}</span>
      },
    },
    // «Контроль КП»: дата следующего контакта с клиентом (внутренний дедлайн
    // менеджера). Закупщику не показываем — колонка продажной линии.
    ...(!isProcurement
      ? [
          {
            field: 'followUpLabel',
            headerName: 'Контакт',
            minWidth: 110,
            filterParams: { dateMode: true },
            comparator: (_a: string, _b: string, nodeA, nodeB) => {
              const da = (nodeA?.data as RowData | undefined)?.followUpDueAt
              const db = (nodeB?.data as RowData | undefined)?.followUpDueAt
              const ta = da ? new Date(da).getTime() : 0
              const tb = db ? new Date(db).getTime() : 0
              return ta - tb
            },
            cellRenderer: ({ data }: { data: RowData }) => {
              if (!data.followUpDueAt) return <span className="text-gray-300">—</span>
              const date = new Date(data.followUpDueAt)
              const now = new Date()
              now.setHours(0, 0, 0, 0)
              date.setHours(0, 0, 0, 0)
              const diffDays = Math.ceil((date.getTime() - now.getTime()) / 86400000)
              const title = data.followUpNote ?? undefined
              if (diffDays < 0)
                return (
                  <span
                    title={title}
                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700"
                  >
                    🔔 {data.followUpLabel}
                  </span>
                )
              if (diffDays === 0)
                return (
                  <span
                    title={title}
                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700"
                  >
                    🔔 {data.followUpLabel}
                  </span>
                )
              return (
                <span title={title} className="text-gray-700">
                  {data.followUpLabel}
                </span>
              )
            },
          } as ColDef,
        ]
      : []),
    {
      field: 'statusLabel',
      headerName: 'Статус',
      minWidth: 140,
      sortable: false,
      cellRenderer: ({ data }: { data: RowData }) => {
        if (isProcurement) {
          const ps = (data._req.procurementStatus ?? 'NEW') as
            | 'NEW'
            | 'PARTIAL'
            | 'IN_PROGRESS'
            | 'READY'
          return <ProcStatusBadge status={ps} />
        }
        return (
          <RequestStatusBadge
            status={data.status}
            isPartiallyProcessed={data._req.isPartiallyProcessed ?? false}
          />
        )
      },
    },
    { field: 'itemsCount', headerName: 'Позиций', minWidth: 80 },
    ...(isProcurement
      ? [
          {
            field: 'procQuotesLabel',
            headerName: 'Квоты',
            minWidth: 100,
            comparator: (_a: string, _b: string, nodeA, nodeB) => {
              const ra = nodeA?.data as RowData | undefined
              const rb = nodeB?.data as RowData | undefined
              const ta = ra?.procQuotesTotal ?? 0
              const tb = rb?.procQuotesTotal ?? 0
              const ma = ra?.procQuotesByMe ?? 0
              const mb = rb?.procQuotesByMe ?? 0
              const pa = ta > 0 ? ma / ta : 0
              const pb = tb > 0 ? mb / tb : 0
              if (pa !== pb) return pa - pb
              return ma - mb
            },
            cellRenderer: ({ data }: { data: RowData }) => {
              const done = data.procQuotesDone
              return (
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                    done ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                  }`}
                >
                  {data.procQuotesLabel}
                </span>
              )
            },
          } as ColDef,
        ]
      : []),
    ...(!isProcurement
      ? [
          {
            field: 'procurementProgressLabel',
            headerName: 'Закупка',
            minWidth: 145,
            cellRenderer: ({ data }: { data: RowData }) => {
              const tone = data.procurementProgressTone
              const cls =
                tone === 'green'
                  ? 'bg-green-100 text-green-700'
                  : tone === 'amber'
                    ? 'bg-amber-100 text-amber-700'
                    : tone === 'blue'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-500'
              return (
                <span className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${cls}`}>
                  {data.procurementProgressLabel}
                </span>
              )
            },
          } as ColDef,
        ]
      : []),
    ...(!isProcurement
      ? [
          {
            field: 'quotationSumLabel',
            headerName: 'Сумма КП',
            minWidth: 130,
            comparator: (_a: string, _b: string, nodeA, nodeB) => {
              const va = (nodeA?.data as RowData | undefined)?.quotationSum
              const vb = (nodeB?.data as RowData | undefined)?.quotationSum
              return (va ?? -1) - (vb ?? -1)
            },
            cellRenderer: ({ data }: { data: RowData }) =>
              data.quotationSum == null ? (
                <span className="text-gray-400 italic">КП нет</span>
              ) : (
                <span className="text-gray-900 font-medium">{data.quotationSumLabel}</span>
              ),
          } as ColDef,
        ]
      : []),
    ...(showManagerColumn
      ? [
          {
            field: 'managerName',
            headerName: 'Менеджер',
            minWidth: 140,
            tooltipField: 'managerName',
          } as ColDef,
        ]
      : []),
    {
      field: 'notes',
      headerName: 'Заметка',
      minWidth: 160,
      sortable: false,
      filter: false,
      // Заметка к самой заявке (поле request.notes) — показываем всем ролям.
      valueGetter: ({ data }: { data: RowData }) => data._req.notes ?? '',
      cellRenderer: ({ data }: { data: RowData }) => {
        const note = data._req.notes?.trim()
        if (!note) return <span className="text-gray-300">—</span>
        return (
          <span className="text-gray-600 truncate" title={note}>
            {note}
          </span>
        )
      },
    },
    {
      field: 'createdAtLabel',
      headerName: 'Создана',
      minWidth: 110,
      filterParams: { dateMode: true },
      comparator: (_a: string, _b: string, nodeA, nodeB) => {
        const da = (nodeA?.data as RowData | undefined)?.createdAt
        const db = (nodeB?.data as RowData | undefined)?.createdAt
        const ta = da ? new Date(da).getTime() : 0
        const tb = db ? new Date(db).getTime() : 0
        return ta - tb
      },
    },
    ...(canDelete
      ? [
          {
            headerName: '',
            width: 50,
            minWidth: 50,
            maxWidth: 50,
            resizable: false,
            sortable: false,
            filter: false,
            suppressMovable: true,
            cellRenderer: ({ data }: { data: RowData }) => (
              <RequestsDeleteButton req={data._req} onDeleted={onDeleted} />
            ),
          } as ColDef,
        ]
      : []),
  ]
}
